import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Title,
    Text,
    Button,
    Alert,
    TextInput,
    Paper,
    Stack,
    Group,
} from '@mantine/core';
import { IconArrowLeft, IconPlayerPlay } from '@tabler/icons-react';
import { ScimEntityService } from '../../services/ScimEntityService';
import { getScimConfig } from '../../utils/envUtils';
import { getPluginFunctionInvokeApiPath } from '../../utils/pluginFunctionUtils';
import { EntityBrowserDialog } from '../dialogs';
import { LoadingState } from '../common/LoadingState';

export interface CatalogInput {
    Name?: string;
    InputType?: string;
    Required?: boolean;
    MaxLength?: number;
    Array?: boolean;
    Password?: boolean;
    IsReference?: boolean;
    ReferenceType?: string;
}

function isReferenceEntityInput(inp: CatalogInput): boolean {
    const type = (inp?.InputType ?? '').trim();
    const isRef = inp?.IsReference === true;
    const refType = (inp?.ReferenceType ?? '').trim();
    return (type === 'TargetEntityId' && isRef && refType.length > 0);
}

export interface FunctionCatalogEntry {
    Id?: string;
    DisplayName?: string;
    Inputs?: CatalogInput[];
    TargetEntities?: string[];
    Registered?: boolean;
}

export interface PrepareInvokeViewProps {
    catalogId: string;
    basePath: string;
    entityService: ScimEntityService;
    pluginFunctionId?: string | null;
    pivotEntityId?: string | null;
    invokeTargetId?: string | null;
    onInvoke?: (catalogId: string, entry: FunctionCatalogEntry, values: Record<string, string>) => void;
}

export const PrepareInvokeView: React.FC<PrepareInvokeViewProps> = ({
    catalogId,
    basePath,
    entityService,
    pluginFunctionId = null,
    pivotEntityId = null,
    invokeTargetId = null,
    onInvoke
}) => {
    const [entry, setEntry] = useState<FunctionCatalogEntry | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [invokeError, setInvokeError] = useState<string | null>(null);
    const [invoking, setInvoking] = useState(false);
    const [invokeResult, setInvokeResult] = useState<any>(null);
    const [entityBrowserForInput, setEntityBrowserForInput] = useState<string | null>(null);
    const [passwordConfirm, setPasswordConfirm] = useState<Record<string, string>>({});

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        entityService
            .getFunctionCatalogEntry(catalogId)
            .then((data: any) => {
                if (!cancelled) {
                    setEntry(data ?? null);
                    const initial: Record<string, string> = {};
                    const inputs = data?.Inputs ?? [];
                    if (Array.isArray(inputs)) {
                        inputs.forEach((inp: CatalogInput) => {
                            const name = inp?.Name;
                            if (name) initial[name] = '';
                        });
                    }
                    setFormValues(initial);
                }
            })
            .catch((err: any) => {
                if (!cancelled) {
                    setError(err?.response?.data?.Message ?? err?.message ?? 'Failed to load function catalog entry.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [entityService, catalogId]);

    const inputs = useMemo(() => entry?.Inputs ?? [], [entry]);
    const requiredNames = useMemo(() => inputs.filter((i) => i.Required).map((i) => i.Name).filter(Boolean) as string[], [inputs]);
    const allRequiredFilled = useMemo(
        () => requiredNames.every((name) => (formValues[name] ?? '').trim() !== ''),
        [requiredNames, formValues]
    );

    const passwordInputNames = useMemo(
        () =>
            inputs
                .filter((i) => i.Password === true && !isReferenceEntityInput(i))
                .map((i) => i.Name)
                .filter(Boolean) as string[],
        [inputs]
    );
    const allPasswordsMatch = useMemo(
        () =>
            passwordInputNames.every((name) => (formValues[name] ?? '') === (passwordConfirm[name] ?? '')),
        [passwordInputNames, formValues, passwordConfirm]
    );

    const handleChange = (name: string, value: string) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
        setInvokeError(null);
    };
    const handlePasswordConfirmChange = (name: string, value: string) => {
        setPasswordConfirm((prev) => ({ ...prev, [name]: value }));
        setInvokeError(null);
    };

    const getErrorFromResponseBody = (data: any): string | null => {
        if (data == null || typeof data !== 'object') return null;
        const code = (data.StatusCode ?? data.statusCode ?? '').toString().trim();
        if (!code) return null;
        const upper = code.toUpperCase();
        if (upper === 'OK' || upper === 'SUCCESS' || upper === '200') return null;
        const msg = (data.Message ?? data.message ?? '').toString().trim();
        return msg || `Request failed (${code}).`;
    };

    const handleInvoke = async () => {
        setInvokeError(null);
        setInvokeResult(null);
        setInvoking(true);
        try {
            const data =
                pluginFunctionId && pivotEntityId
                    ? await entityService.invokePluginFunction(pluginFunctionId, pivotEntityId, invokeTargetId ?? catalogId, formValues)
                    : await entityService.invokeFunctionCatalog(catalogId, formValues);
            const bodyError = getErrorFromResponseBody(data);
            if (bodyError) {
                setInvokeError(bodyError);
            } else {
                setInvokeResult(data);
                onInvoke?.(catalogId, entry!, formValues);
                setFormValues((prev) => {
                    const next = { ...prev };
                    passwordInputNames.forEach((name) => delete next[name]);
                    return next;
                });
                setPasswordConfirm((prev) => {
                    const next = { ...prev };
                    passwordInputNames.forEach((name) => delete next[name]);
                    return next;
                });
            }
        } catch (e: any) {
            setInvokeError(e?.response?.data?.Message ?? e?.message ?? 'Invoke failed.');
        } finally {
            setInvoking(false);
        }
    };

    const handleBack = () => {
        if (pluginFunctionId && pivotEntityId) {
            window.location.hash = `${basePath}/${encodeURIComponent(pluginFunctionId)}/${encodeURIComponent(pivotEntityId)}`;
        } else {
            window.location.hash = basePath;
        }
    };

    if (loading) {
        return (
            <LoadingState variant="centered" label="Loading function definition…" size={24} />
        );
    }

    if (error || !entry) {
        return (
            <Box py="md">
                <Button size="xs" leftSection={<IconArrowLeft size={16} />} onClick={handleBack} mb="md" variant="subtle">
                    Back
                </Button>
                <Alert color="red">{error ?? 'Function catalog entry not found.'}</Alert>
            </Box>
        );
    }

    return (
        <Box py="md">
            <Group gap="xs" mb="md" wrap="wrap">
                <Button size="xs" leftSection={<IconArrowLeft size={16} />} onClick={handleBack} variant="subtle">
                    Back
                </Button>
            </Group>
            <Title order={6} mb="xs">
                {entry.DisplayName ?? entry.Id ?? 'Prepare invoke'}
            </Title>
            {entry.TargetEntities && entry.TargetEntities.length > 0 && (
                <Text size="sm" c="dimmed" mb="md">
                    Target entities: {entry.TargetEntities.join(', ')}
                </Text>
            )}
            {invokeError && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setInvokeError(null)}>
                    {invokeError}
                </Alert>
            )}
            {invokeResult != null && (
                <Alert color="green" mb="md" withCloseButton onClose={() => setInvokeResult(null)}>
                    Invoke completed. See response below.
                </Alert>
            )}
            {inputs.length === 0 ? (
                <Text size="sm" c="dimmed">No inputs defined.</Text>
            ) : (
                <Paper withBorder p="md" maw={560}>
                    <Stack gap="md">
                        {inputs.map((inp, idx) => {
                            const name = inp.Name ?? `Input_${idx}`;
                            const isRequired = inp.Required === true;
                            const isPassword = inp.Password === true && !isReferenceEntityInput(inp);
                            const maxLength = inp.MaxLength;
                            const refType = inp.ReferenceType;
                            const label = refType ? `${name} (${refType})` : name;
                            const useEntitySelector = isReferenceEntityInput(inp);
                            const entityType = (refType ?? '').trim() || 'Group';

                            if (useEntitySelector) {
                                return (
                                    <Box key={name}>
                                        <TextInput
                                            label={label}
                                            required={isRequired}
                                            value={formValues[name] ?? ''}
                                            placeholder="No entity selected"
                                            readOnly
                                            description={inp.Array ? 'Array value' : `Select a ${entityType}`}
                                            rightSection={
                                                <Group gap="xs" mr="xs">
                                                    <Button
                                                        variant="outline"
                                                        size="xs"
                                                        onClick={() => setEntityBrowserForInput(name)}
                                                    >
                                                        Select
                                                    </Button>
                                                    {(formValues[name] ?? '').trim() && (
                                                        <Button
                                                            variant="outline"
                                                            size="xs"
                                                            color="red"
                                                            onClick={() => handleChange(name, '')}
                                                        >
                                                            Clear
                                                        </Button>
                                                    )}
                                                </Group>
                                            }
                                        />
                                    </Box>
                                );
                            }

                            return (
                                <React.Fragment key={name}>
                                    <TextInput
                                        label={label}
                                        required={isRequired}
                                        type={isPassword ? 'password' : 'text'}
                                        value={formValues[name] ?? ''}
                                        onChange={(e) => handleChange(name, e.currentTarget.value)}
                                        maxLength={maxLength}
                                        description={inp.Array ? 'Array value' : undefined}
                                        mb={isPassword ? 'xs' : undefined}
                                    />
                                    {isPassword && (
                                        <TextInput
                                            label={`Confirm ${name}`}
                                            required={isRequired}
                                            type="password"
                                            value={passwordConfirm[name] ?? ''}
                                            onChange={(e) => handlePasswordConfirmChange(name, e.currentTarget.value)}
                                            maxLength={maxLength}
                                            error={
                                                (passwordConfirm[name] ?? '') !== '' &&
                                                (formValues[name] ?? '') !== (passwordConfirm[name] ?? '')
                                                    ? 'Does not match'
                                                    : undefined
                                            }
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                        <Button
                            loading={invoking}
                            leftSection={<IconPlayerPlay size={16} />}
                            onClick={handleInvoke}
                            disabled={!allRequiredFilled || !allPasswordsMatch}
                        >
                            {invoking ? 'Invoking…' : 'Invoke'}
                        </Button>
                        {invokeResult != null && (
                            <Box mt="md">
                                {typeof invokeResult === 'object' &&
                                invokeResult !== null &&
                                (invokeResult.StatusCode ?? invokeResult.statusCode)?.toString().toUpperCase() === 'OK' ? (
                                    <>
                                        {(invokeResult.Message ?? invokeResult.message) != null && (
                                            <Alert color="green" mb="md" withCloseButton onClose={() => setInvokeResult(null)}>
                                                {String(invokeResult.Message ?? invokeResult.message)}
                                            </Alert>
                                        )}
                                        {invokeResult.Result != null &&
                                            typeof invokeResult.Result === 'object' &&
                                            Object.keys(invokeResult.Result).length > 0 && (
                                                <>
                                                    <Text size="sm" fw={600} mb="xs">Result</Text>
                                                    <Paper withBorder p="md" bg="var(--mantine-color-default-hover)" style={{ overflow: 'auto' }}>
                                                        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            {JSON.stringify(invokeResult.Result, null, 2)}
                                                        </pre>
                                                    </Paper>
                                                </>
                                            )}
                                    </>
                                ) : (
                                    <>
                                        <Text size="sm" fw={600} mb="xs">Response</Text>
                                        <Paper withBorder p="md" bg="var(--mantine-color-default-hover)" style={{ overflow: 'auto' }}>
                                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {typeof invokeResult === 'object' ? JSON.stringify(invokeResult, null, 2) : String(invokeResult)}
                                            </pre>
                                        </Paper>
                                    </>
                                )}
                            </Box>
                        )}
                    </Stack>

                    {entityBrowserForInput != null && (() => {
                        const inp = inputs.find((i) => (i.Name ?? '') === entityBrowserForInput);
                        const refType = (inp?.ReferenceType ?? '').trim() || 'Group';
                        return (
                            <EntityBrowserDialog
                                open={true}
                                onClose={() => setEntityBrowserForInput(null)}
                                onConfirm={(selectedEntities) => {
                                    if (selectedEntities.length > 0 && entityBrowserForInput) {
                                        handleChange(entityBrowserForInput, selectedEntities[0].Id);
                                    }
                                    setEntityBrowserForInput(null);
                                }}
                                entityType={refType}
                                title={`Select ${refType}`}
                                callerName="PrepareInvokeView"
                                singleSelection={true}
                            />
                        );
                    })()}
                </Paper>
            )}
            {pluginFunctionId && pivotEntityId && (
                <Text
                    component="div"
                    size="xs"
                    c="dimmed"
                    ff="monospace"
                    style={{ marginTop: 16, wordBreak: 'break-all' }}
                >
                    {getScimConfig().apiUrl.replace(/\/$/, '')}
                    {getPluginFunctionInvokeApiPath(pluginFunctionId, pivotEntityId, invokeTargetId ?? catalogId)}
                </Text>
            )}
        </Box>
    );
};
